/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, ChangeEvent } from 'react';
import {
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  ButtonGroup,
  LoadingButton,
  Button,
  Link,
  Typography,
  TextField,
  Alert,
} from '@one-thd/sui-atomic-components';
import { DRAWER_TEXT } from './otp-text-constants';
import { OTP_ERROR_SOURCES, OTP_ERROR_CODES, OTP_ERROR_MESSAGES, type OTPErrorSource } from '../../utils/consts';
import { validateVerificationCode } from '../../utils/otp-utils';
import { getCustomerInfo } from '../../utils/general';
import { useGiftCardBarcodeService } from '../../services/gift-card-barcode-service';
import { IGiftCardCommonData } from '../../models/common-models';

interface ButtonConfig {
  inline: {
    text: string;
    channel: string;
  };
  separate?: {
    text: string;
    channel: string;
  };
}

interface DrawerTexts {
  title: string;
  header: string;
  verificationCode: string;
  verifyButton: string;
  cancelButton: string;
  refreshCode: string;
}

interface OTPDrawerContentProps {
  drawerTexts: DrawerTexts;
  contactMethodToUse: string;
  verificationCode: string;
  validationError: string | null;
  buttonsConfig: ButtonConfig;
  otpErrorMessage?: string;
  otpErrorSource?: OTPErrorSource | null;
  onVerificationCodeChange: (value: string) => void;
  onInlineButtonClick: () => void;
  onSeparateButtonClick: () => void;
  // Footer props
  onVerifyClick?: () => void;
  onCloseClick: () => void;
  isVerifyDisabled: boolean;
  isLoading: boolean;
  // New props for combined OTP verification and barcode retrieval
  shouldShowOTPContent?: boolean;
  selectedGiftCardPaymentIDs?: string[];
  otpHook?: {
    userEmail: string | null;
    authID: string | null;
    setValidationError: (error: string) => void;
    setOTPDisabled?: (disabled: boolean) => void;
    setOtpError?: (message: string, source: OTPErrorSource) => void;
  };
  onOTPVerified?: (verified: boolean) => void;
  onBarcodeDataReceived?: (barcodeData: Partial<IGiftCardCommonData>[]) => void;
  setIsBarcodeLoading?: (loading: boolean) => void;
}

/**
 * Pure component for rendering OTP drawer content
 * Contains only the UI logic, no business logic
 */
export const OTPDrawerContent: FC<OTPDrawerContentProps> = ({
  drawerTexts,
  contactMethodToUse,
  verificationCode,
  validationError,
  buttonsConfig,
  otpErrorMessage,
  otpErrorSource,
  onVerificationCodeChange,
  onInlineButtonClick,
  onSeparateButtonClick,
  onVerifyClick,
  onCloseClick,
  isVerifyDisabled,
  isLoading,
  shouldShowOTPContent,
  selectedGiftCardPaymentIDs,
  otpHook,
  onOTPVerified,
  onBarcodeDataReceived,
  setIsBarcodeLoading
}) => {
  const { getGiftCardBarcodes } = useGiftCardBarcodeService();
  const { customerAccountId } = getCustomerInfo();

  const handleVerifyClick = async () => {
    // Check if we should use the combined flow (OTP verification + barcode retrieval)
    if (shouldShowOTPContent && selectedGiftCardPaymentIDs && otpHook && onOTPVerified && onBarcodeDataReceived) {
      // Combined flow: validate OTP and call getGiftCardBarcode
      const codeValidation = validateVerificationCode(verificationCode);
      if (!codeValidation.isValid) {
        otpHook.setValidationError(codeValidation.error || 'Invalid verification code');
        return;
      }

      // Validate required authentication information
      if (!otpHook.userEmail || !otpHook.authID || !customerAccountId) {
        otpHook.setValidationError('Missing required authentication information');
        return;
      }

      try {
        // Set loading state before API call
        setIsBarcodeLoading?.(true);

        // Call getGiftCardBarcode with OTP verification input
        const result = await getGiftCardBarcodes(
          selectedGiftCardPaymentIDs,
          {
            email: otpHook.userEmail,
            encryptedPayload: otpHook.authID,
            otp: verificationCode
          }
        );

        if (result.success && result.barcodeData) {
          // Successfully retrieved barcode data
          onBarcodeDataReceived(result.barcodeData);
          onOTPVerified(true);

          // Clear verification code only on success
          onVerificationCodeChange('');
        } else if (result.otpVerified === false) {
          // Handle OTP verification failure - these are validation errors
          // Clear verification code when OTP verification fails
          onVerificationCodeChange('');

          // Check if this is a max attempts error
          const isMaxAttemptsError = result.errorCode
            && OTP_ERROR_CODES.MAX_ATTEMPTS_EXCEEDED.includes(result.errorCode);

          if (isMaxAttemptsError) {
            // Handle max attempts exceeded
            otpHook.setValidationError('Send new OTP Code, Maximum attempts reached');
            if (otpHook.setOTPDisabled) {
              otpHook.setOTPDisabled(true);
            }
          } else if (result.remainingCount !== undefined) {
            // Handle invalid code with remaining attempts
            otpHook.setValidationError('Invalid code. Resend or try a different code.');
          } else {
            // Handle other verification failures
            otpHook.setValidationError(result.error || 'Invalid verification code');
          }
        } else {
          // Handle unexpected errors or missing data - these are general errors
          // This covers cases where:
          // - result.success is false but otpVerified is undefined (not an OTP error)
          // - result.barcodeData is missing
          // - Any other unexpected response structure
          // Set general error instead of validation error
          otpHook.setOtpError?.(OTP_ERROR_MESSAGES.GENERAL_FAILURE, OTP_ERROR_SOURCES.VERIFY);
        }
      } catch (error) {
        // Handle exceptions during the API call (network errors, etc.)
        // Set general error instead of validation error
        otpHook.setOtpError?.(OTP_ERROR_MESSAGES.GENERAL_FAILURE, OTP_ERROR_SOURCES.VERIFY);
      } finally {
        // Clear loading state after API call completes
        setIsBarcodeLoading?.(false);
      }
    } else if (onVerifyClick) {
      // Fallback to original verify click handler
      onVerifyClick();
    }
  };
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onVerificationCodeChange(event.target.value);
  };

  // For getOTPOptions or getOTPCode errors, show only the Alert error
  // if (otpErrorMessage && (otpErrorSource === OTP_ERROR_SOURCES.OPTIONS ||
  //     otpErrorSource === OTP_ERROR_SOURCES.CODE)) {
  if (otpErrorMessage && (otpErrorSource === OTP_ERROR_SOURCES.OPTIONS)) {
    return (
      <>
        <DrawerHeader title="Verification Required" />
        <DrawerBody>
          <Alert status="error" size="large" data-testid="otp-error">
            {otpErrorMessage}
          </Alert>
        </DrawerBody>
        <DrawerFooter>
          <Button
            variant="secondary"
            onClick={onCloseClick}
          >
            Close
          </Button>
        </DrawerFooter>
      </>
    );
  }

  return (
    <>
      <DrawerHeader title={drawerTexts.title} />
      <DrawerBody>
        <>
          {otpErrorMessage && (
            <Alert status="error" size="large" data-testid="otp-error">
              {otpErrorMessage}
            </Alert>
          )}
          <Typography variant="body-base">
            {drawerTexts.header}
          </Typography>
          <Typography variant="body-base" weight="bold">
            {contactMethodToUse}
          </Typography>
          <div className="sui-my-4">
            <TextField
              placeholder={DRAWER_TEXT.CODE_PLACEHOLDER}
              value={verificationCode}
              type="number"
              onChange={handleInputChange}
              label={drawerTexts.verificationCode}
              status={validationError ? 'error' : ''}
              statusMessage={validationError}
              id="otp-input"
              required
              autoFocus
            />
          </div>
          <div className="sui-flex sui-items-center sui-gap-2 sui-py-4">
            <Typography>
              {drawerTexts.refreshCode}
            </Typography>
            <Link
              component="button"
              onClick={onInlineButtonClick}
            >
              {buttonsConfig.inline.text}
            </Link>
          </div>
          {buttonsConfig.separate && (
            <Link
              component="button"
              onClick={onSeparateButtonClick}
            >
              {buttonsConfig.separate.text}
            </Link>
          )}
        </>
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup orientation="vertical" variant="primary">
          <LoadingButton
            variant="primary"
            onClick={handleVerifyClick}
            disabled={isVerifyDisabled}
            loading={isLoading}
          >
            {drawerTexts.verifyButton}
          </LoadingButton>
          <Button
            variant="secondary"
            onClick={onCloseClick}
          >
            {drawerTexts.cancelButton}
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
};
